import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import Icon from '../../components/forms/icon.component';
import { usePermissions } from '../../hooks/use-permissions.hook';
import { useDarkMode } from '../../hooks/use-dark-mode.hook';
import {
  useDrawerCleanupOnRouteChange,
  useDrawerListeners
} from '../../hooks/use-drawer-cleanup.hook';

const routes = {
  dashboard: '/dashboard',
  reports: '/reports',
  cpd_templates: '/cpd-templates',
  cpd_operators: '/cpd-operators',
  cpd_routes: '/cpd-routes',
  cpd_overrides: '/cpd-overrides',
  routes_outgoing: '/routes-outgoing',
  routes_incoming: '/routes-incomming',
  dialplan: '/dialplan',
  users: '/users',
  audit: '/audit',
  manual: '/manual'
} as const;

const MenuItem = ({
  to,
  icon,
  title,
  isActive,
  badge,
  iconSize = 'fs-2x',
  className = ''
}: {
  to: string;
  icon: string;
  title: string | React.ReactNode;
  isActive: boolean;
  badge?: React.ReactNode;
  iconSize?: string;
  className?: string;
}) => (
  <div className={`menu-item ms-3 b-radius hover-scale ${className}`}>
    <Link className={`menu-link b-radius mw-225px ${isActive ? 'active' : ''}`} to={to}>
      <span className="menu-icon">
        <Icon icon={icon} size={iconSize} className="text-dark hover-icon" />
      </span>
      <span className="menu-title">
        {title}
        {badge}
      </span>
    </Link>
  </div>
);

const MenuSection = ({ title }: { title: string }) => (
  <div className="menu-item pt-5 ms-3">
    <div className="menu-content">
      <span className="fw-bolder text-muted text-uppercase fs-7">{title}</span>
    </div>
  </div>
);

const SubMenuItem = ({ to, title, isActive }: { to: string; title: string; isActive: boolean }) => (
  <div className="menu-item hover-scale ms-3 b-radius">
    <Link className={`menu-link b-radius ${isActive ? 'active' : ''}`} to={to}>
      <span className="menu-bullet">
        <span className="bullet bullet-dot" />
      </span>
      <span className="menu-title">{title}</span>
    </Link>
  </div>
);

function Aside() {
  const { t } = useTranslation();
  const location = useLocation();
  const isDarkTheme = useDarkMode();

  const { isSuperAdmin, canView, canWrite } = usePermissions();
  const homeRoute = routes.dashboard;

  const isRouteActive = useMemo(() => {
    const path = location.pathname;
    const isEq = (p: string) => path === p;
    return {
      dashboard: isEq('/dashboard'),
      reports: isEq('/reports'),
      cpd_templates: isEq('/cpd-templates'),
      cpd_operators: isEq('/cpd-operators'),
      cpd_routes: isEq('/cpd-routes'),
      cpd_overrides: isEq('/cpd-overrides'),
      routes_outgoing: isEq('/routes-outgoing'),
      routes_incoming: isEq('/routes-incomming'),
      dialplan: isEq('/dialplan'),
      users: isEq('/users'),
      audit: isEq('/audit'),
      manual: path === '/manual' || path.startsWith('/manual/')
    };
  }, [location.pathname]);

  useDrawerCleanupOnRouteChange(location.pathname);
  useDrawerListeners();

  return (
    <div
      id="kt_aside"
      className="aside aside-default aside-hoverable overflow-x-hidden"
      data-kt-drawer="true"
      data-kt-drawer-name="aside"
      data-kt-drawer-activate="{default: true, lg: false}"
      data-kt-drawer-overlay="true"
      data-kt-drawer-width="{default:'200px', '300px': '250px'}"
      data-kt-drawer-direction="start"
      data-kt-drawer-toggle="#kt_aside_toggle"
    >
      {/* Logo */}
      <div className="aside-logo flex-column-auto px-10 pt-9 pb-5" id="kt_aside_logo">
        <Link to={homeRoute}>
          <img
            alt="Logo"
            src={`/assets/media/logos/${isDarkTheme ? 'rap-logo-dark.png' : 'rap-logo.png'}`}
            className="w-145px logo-default ms-3 mt-4"
            height="50"
          />
          <img
            alt="Logo"
            src={`/assets/media/logos/${isDarkTheme ? 'rap-logo-circle-dark.svg' : 'rap-logo-circle.svg'}`}
            className="w-55px logo-minimize"
            width="55"
            height="60"
          />
        </Link>
      </div>

      {/* Menu */}
      <div className="aside-menu flex-column-fluid ps-3 pe-1">
        <div
          className="menu menu-sub-indention menu-column menu-rounded menu-title-gray-600 menu-icon-gray-500 menu-active-bg menu-state-primary menu-arrow-gray-500 fw-semibold fs-6 my-5 mt-lg-2 mb-lg-0"
          id="kt_aside_menu"
          data-kt-menu="true"
        >
          <div
            className="hover-scroll-y mx-4 overflow-x-hidden"
            id="kt_aside_menu_wrapper"
            data-kt-scroll="true"
            data-kt-scroll-activate="{default: false, lg: true}"
            data-kt-scroll-height="auto"
            data-kt-scroll-wrappers="#kt_aside_menu"
            data-kt-scroll-offset="40px"
            data-kt-scroll-dependencies="#kt_aside_logo, #kt_aside_footer"
          >
            <MenuItem
              to={routes.dashboard}
              icon="ki-chart-pie-3"
              title={t('asaid.link-dashboard')}
              isActive={isRouteActive.dashboard}
            />

            <MenuItem
              to={routes.reports}
              icon="ki-document"
              title="Reportes"
              isActive={isRouteActive.reports}
            />

            <MenuSection title="CPD (Call Progress Detection)" />

            <div
              className={`menu-item menu-accordion ms-3 b-radius mb-1 ${isRouteActive.cpd_templates || isRouteActive.cpd_operators || isRouteActive.cpd_routes || isRouteActive.cpd_overrides ? 'hover show' : ''}`}
              data-kt-menu-trigger="click"
            >
              <span
                className={`menu-link b-radius ${isRouteActive.cpd_templates || isRouteActive.cpd_operators || isRouteActive.cpd_routes || isRouteActive.cpd_overrides ? 'active' : ''}`}
              >
                <span className="menu-icon">
                  <Icon icon="ki-abstract-26" size="fs-2" className="text-dark hover-icon" />
                </span>
                <span className="menu-title">CPD</span>
                <span className="menu-arrow" />
              </span>

              <div className="menu-sub menu-sub-accordion ps-5">
                <SubMenuItem
                  to={routes.cpd_templates}
                  title="Templates"
                  isActive={isRouteActive.cpd_templates}
                />
                <SubMenuItem
                  to={routes.cpd_operators}
                  title="Operators"
                  isActive={isRouteActive.cpd_operators}
                />
                <SubMenuItem
                  to={routes.cpd_routes}
                  title="Routes"
                  isActive={isRouteActive.cpd_routes}
                />
                <SubMenuItem
                  to={routes.cpd_overrides}
                  title="Overrides"
                  isActive={isRouteActive.cpd_overrides}
                />
              </div>
            </div>

            <MenuSection title="Rutas (OpenSIPS)" />

            <MenuItem
              to={routes.routes_incoming}
              icon="ki-arrow-down-right"
              title="Entrantes"
              isActive={isRouteActive.routes_incoming}
            />

            <MenuItem
              to={routes.routes_outgoing}
              icon="ki-arrow-up-right"
              title="Salientes"
              isActive={isRouteActive.routes_outgoing}
            />

            <MenuSection title="Dialplan" />

            <MenuItem
              to={routes.dialplan}
              icon="ki-element-2"
              title="Dialplan"
              isActive={isRouteActive.dialplan}
            />

            {canView('users') && (
              <>
                <MenuSection title={t('users-page.title-page')} />

                <MenuItem
                  to={routes.users}
                  icon="ki-profile-user"
                  title={t('users-page.title-page')}
                  isActive={isRouteActive.users}
                />
              </>
            )}

            {canView('audit') && (
              <MenuItem
                to={routes.audit}
                icon="ki-shield-search"
                title="Auditoría"
                isActive={isRouteActive.audit}
              />
            )}

            <MenuSection title="Ayuda" />

            <MenuItem
              to={routes.manual}
              icon="ki-book-open"
              title="Manual"
              isActive={isRouteActive.manual}
            />
          </div>
        </div>
      </div>

      <div id="kt_aside_footer" className="aside-footer mt-5 bg-pw">
        <div className="d-flex align-items-center justify-content-center">
          <span
            className="text-muted fw-bolder fs-6 d-flex align-items-center"
            style={{ whiteSpace: 'nowrap' }}
          >
            <span className="text-primary">{t('general.powered-by')}</span>
            <img
              src={`/assets/media/logos/voices-gnl${isDarkTheme ? '-dark' : ''}.png`}
              alt="Voices"
              title="Voices"
              height="18"
              className="ms-2"
            />
          </span>
        </div>
      </div>
    </div>
  );
}

export default Aside;
